"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, FileCheck2, Wind, PhoneCall, ArrowRight, Hammer, Biohazard, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
export default function WhyChoose() {
  const shouldReduceMotion = useReducedMotion();
  const specifications = [{
    title: "In-house containment crews",
    desc: "Zero subcontracted liability. Our own certified technicians set negative-pressure HEPA barriers and manage removal directly.",
    icon: ShieldCheck
  }, {
    title: "Compliant disposal manifests",
    desc: "Every pound of hazardous material is sealed, labeled, and tracked with signed state and regional disposal manifests.",
    icon: FileCheck2
  }, {
    title: "Third-party air clearance",
    desc: "Independent industrial hygiene testing confirms zero airborne fiber migration before your structure is cleared for re-entry.",
    icon: Wind
  }];
  const relatedServices = [{
    title: "Reconstruction & Design-Build",
    to: "/ReconstructionDesignBuildPage",
    desc: "Seamless post-abatement drywall repair, structural framing, and turnkey architectural rebuilding under one responsible contractor.",
    icon: Hammer,
    badge: "Turnkey Recovery"
  }, {
    title: "Sewage Cleanup",
    to: "/SewageCleanupPage",
    desc: "Immediate Category 3 blackwater extraction, antimicrobial sanitation, and rapid structural decontamination for Mid-Atlantic properties.",
    icon: Biohazard,
    badge: "Category 3 Certified"
  }];
  return <section id="why-choose-triserve-related-services" data-nav="dark" className="dark bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Operational Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 pb-8 mb-12 border-b border-border text-xs font-mono tracking-wide uppercase text-muted-foreground">
          <div className="flex items-center gap-2">
            <span className="inline-block size-2 rounded-full bg-accent animate-pulse" />
            <span>Operational Integrity Protocol • MD / DC / NOVA</span>
          </div>
          <div className="flex items-center gap-2">
            <span>Direct Dispatch:</span>
            <a href="tel:855-435-6631" className="text-foreground hover:text-accent font-semibold transition-colors">
              855-435-6631
            </a>
          </div>
        </div>

        {/* Main Content Grid: Image + Authority Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center mb-20">
          {/* Visual Column */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          ease: "easeOut"
        }} className="lg:col-span-5 relative">
            <div className="relative rounded-lg overflow-hidden border border-border bg-card shadow-lg">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/hazardous_material_containment_disposal.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/hazardous_material_containment_disposal.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/hazardous_material_containment_disposal.png-wvc-sizes" alt="TriServe environmental technician loading labeled hazardous waste disposal bags into certified containment vehicle" loading="lazy" decoding="async" className="w-full h-80 sm:h-96 lg:h-[420px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 p-3 rounded-md bg-background/90 backdrop-blur-sm border border-border text-xs font-mono flex items-center justify-between text-foreground">
                <span className="flex items-center gap-1.5 font-medium">
                  <CheckCircle2 className="size-4 text-primary" />
                  EPA &amp; OSHA Compliant Chain of Custody
                </span>
                <span className="text-muted-foreground hidden sm:inline">Active Site Protocol</span>
              </div>
            </div>
          </motion.div>

          {/* Core Copy Column */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: 0.1,
          ease: "easeOut"
        }} className="lg:col-span-7 flex flex-col justify-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider mb-4 self-start border border-border">
              Direct Accountability
            </div>

            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground mb-4">
              Direct remediation teams without subcontracted liability
            </h2>

            <p className="text-base sm:text-lg text-muted-foreground mb-6 font-medium">
              We dispatch licensed in-house technicians who contain, remove, and document hazardous materials under strict regional oversight.
            </p>

            <p className="text-sm sm:text-base text-muted-foreground mb-8 leading-relaxed">
              When dealing with regulated lead-based paint and friable asbestos in Maryland, Washington DC, or Northern Virginia, handing project custody to unverified sub-crews introduces unacceptable compliance risks. TriServe conducts every stage under direct internal command. From installing airtight 6-mil poly containment and negative-air HEPA scrubbers to delivering signed regional disposal manifests and passing independent third-party air clearance, we protect your occupants and legal standing throughout.
            </p>

            {/* Micro spec items */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8 pt-6 border-t border-border">
              {specifications.map((spec, i) => {
              const IconComponent = spec.icon;
              return <div key={spec.title} data-index={i} className="flex flex-col gap-1.5">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 rounded-md bg-secondary text-secondary-foreground border border-border">
                        <IconComponent className="size-4 text-primary" />
                      </div>
                      <h3 className="text-sm font-semibold text-foreground">{spec.title}</h3>
                    </div>
                    <p className="text-xs text-muted-foreground leading-normal">
                      {spec.desc}
                    </p>
                  </div>;
            })}
            </div>

            {/* Action Bar */}
            <div className="flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                <a href="tel:855-435-6631">
                  <PhoneCall className="size-4" />
                  <span>Call 855-435-6631 for Clearance</span>
                </a>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-border hover:bg-secondary">
                <Link to="/ContactRequestServicePage">
                  <span>Schedule Site Assessment</span>
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>

        {/* Related Services Grid */}
        <div className="pt-12 border-t border-border">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
            <div>
              <p className="text-xs font-mono uppercase tracking-wider text-accent mb-2">
                Integrated Disaster Capabilities
              </p>
              <h3 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">
                Complementary recovery services
              </h3>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              Property restoration frequently extends past hazardous abatement into structural repair and biological decontamination.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {relatedServices.map((service, i) => {
            const ServiceIcon = service.icon;
            return <motion.div key={service.title} data-index={i} initial={shouldReduceMotion ? false : {
              opacity: 0,
              y: 12
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.35,
              delay: i * 0.1,
              ease: "easeOut"
            }}>
                  <Card className="h-full bg-card text-card-foreground border border-border hover:border-primary/50 transition-[border-color,box-shadow] duration-200">
                    <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-3">
                      <div className="p-2.5 rounded-md bg-secondary text-secondary-foreground border border-border">
                        <ServiceIcon className="size-6 text-primary" />
                      </div>
                      <span className="text-xs font-mono py-0.5 px-2.5 rounded-full bg-muted text-muted-foreground border border-border">
                        {service.badge}
                      </span>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <CardTitle className="text-lg font-bold text-foreground">
                        {service.title}
                      </CardTitle>
                      <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                        {service.desc}
                      </CardDescription>
                    </CardContent>
                    <CardFooter className="pt-2">
                      <Button asChild variant="ghost" size="sm" className="px-0 text-primary hover:text-primary/80 hover:bg-transparent">
                        <Link to={service.to} className="inline-flex items-center gap-1.5 font-semibold text-sm">
                          <span>View service specifications</span>
                          <ArrowRight className="size-4" />
                        </Link>
                      </Button>
                    </CardFooter>
                  </Card>
                </motion.div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}